// Service for managing personal StackSketch API keys (used by the MCP server / CLI).
// These are distinct from the client-side AI provider keys in apiKeyService.ts.

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

export interface ApiKeyMetadata {
  id: string;
  name: string;
  keyPrefix: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}

// Returned only by create — `key` is the plaintext, shown exactly once.
export interface CreatedApiKey extends ApiKeyMetadata {
  key: string;
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Request failed (${res.status})`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export async function listApiKeys(): Promise<ApiKeyMetadata[]> {
  const res = await fetch(`${API_URL}/api/api-keys`, { credentials: 'include' });
  const data = await handle<{ apiKeys: ApiKeyMetadata[] }>(res);
  return data.apiKeys;
}

export async function createApiKey(
  name: string,
  expiresInDays?: number
): Promise<CreatedApiKey> {
  const res = await fetch(`${API_URL}/api/api-keys`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, ...(expiresInDays ? { expiresInDays } : {}) }),
  });
  return handle<CreatedApiKey>(res);
}

export async function revokeApiKey(id: string): Promise<void> {
  const res = await fetch(`${API_URL}/api/api-keys/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  await handle<void>(res);
}
